import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiListings, Listing, LocalityInfo, SearchQueryType } from "../../api/listings-client";
import { capitalizeFirstLetters } from '../../util/AddressUtil';
import Footer from '../Footer';
import MainHeader from '../MainHeader';
import ListingResultsView from '../ListingResultsView';
import { SharedSearchBar } from '../SearchBar';
import SearchOverlay from '../SearchOverlay';

const PAGE_SIZE = 9;

interface Suggestion {
    id: string;
    localityName: string;
    state: string;
    postcode: string;
}

export default function SearchResults() {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchState, setSearchState] = useState<{
        listings: Array<Listing>;
        localityInfo: Array<LocalityInfo>;
        pagination: {
            currentPage: number;
            totalPages: number;
            totalItems: number;
            snapshotTimestamp: string;
        } | null;
        snapshot: string | undefined;
    }>({
        listings: [],
        localityInfo: [],
        pagination: null,
        snapshot: undefined
    });

    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const isMobile = window.innerWidth < 640;
    const scrollAnimationRef = useRef<number>();
    const isAnimatingRef = useRef(false);
    
    // Add a ref to track previous URL params for comparison
    const prevLocationRef = useRef(location.search);

    const handleSearchFocus = () => {
        if (isMobile) {
            setIsSearchOpen(true);
        }
    };

    const handleItemsChange = (newItems) => {
        setSelectedItems(newItems);
    };

    const { localities, currentPage } = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return {
            localities: searchParams.get('localities')?.split(',') || [],
            currentPage: parseInt(searchParams.get('page') || '1'),
        };
    }, [location.search]);

    // Reset snapshot when search parameters (except page) change
    useEffect(() => {
        const prevParams = new URLSearchParams(prevLocationRef.current);
        const prevLocalities = prevParams.get('localities') || '';
        const currentLocalities = new URLSearchParams(location.search).get('localities') || '';
        
        // Only reset snapshot when localities change, not when just the page changes
        if (prevLocalities !== currentLocalities) {
            setSearchState(prev => ({
                ...prev,
                snapshot: undefined
            }));
        }
        
        // Update ref with current location search
        prevLocationRef.current = location.search;
    }, [location.search]);

    const createSuggestionFromLocalityInfo = (info: LocalityInfo): Suggestion => ({
        id: info.id || '',
        localityName: info.name || '',
        state: info.state || '',
        postcode: info.postcode || ''
    });

    useEffect(() => {
        // Clear previous listings when changing pages to avoid stale UI
        setSearchState(prev => ({
            ...prev,
            listings: [],
            pagination: null
        }));
        
        async function fetchListings() {
            try {
                setIsLoading(true);
                const response = await getApiListings({
                    localities,
                    page: currentPage,
                    limit: PAGE_SIZE,
                    snapshot: searchState.snapshot
                });

                setSearchState(prev => ({
                    listings: response.listings || [],
                    localityInfo: response.localityInfo || [],
                    pagination: response.pagination || null,
                    snapshot: response.pagination?.snapshotTimestamp // Just set directly from response
                }));
            } catch (error) {
                console.error('Error fetching listings:', error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchListings();
    }, [localities, currentPage]); // Remove searchState.snapshot from dependencies

    useEffect(() => {
        if (searchState.localityInfo.length > 0) {
            const suggestions = searchState.localityInfo.map(createSuggestionFromLocalityInfo);
            setSelectedItems(suggestions);
        }
    }, [searchState.localityInfo]);

    // Cleanup effect for animation
    useEffect(() => {
        return () => {
            if (scrollAnimationRef.current) {
                cancelAnimationFrame(scrollAnimationRef.current);
            }
            isAnimatingRef.current = false;
        };
    }, []);

    const handlePageChange = (page: number) => {
        // Set loading to true immediately to show loading state
        setIsLoading(true);
        
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('page', page.toString());
        navigate(`/search-results?${searchParams.toString()}`, { replace: true });
    };

    const handleSearch = (items = selectedItems) => {
        const localityIds = items.map(item => item.id);
        const searchParams = new URLSearchParams();
        searchParams.append('localities', localityIds.join(','));
        searchParams.set('page', '1'); // Reset to first page on new search
        const newUrl = `/search-results?${searchParams.toString()}`;

        // Only navigate if the URL is different
        if (location.search !== `?${searchParams.toString()}`) {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant'
            });
            navigate(newUrl);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <MainHeader />
            <meta name="viewport" content="initial-scale=1, width=device-width" />

            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="py-6">
                        <SharedSearchBar
                            selectedItems={selectedItems}
                            onItemsChange={handleItemsChange}
                            onFocus={handleSearchFocus}
                            onSearch={handleSearch}
                        />
                    </div>
                </div>
            </div>

            <SearchOverlay
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                selectedItems={selectedItems}
                onItemsChange={handleItemsChange}
            />

            <div className="pt-6 pb-12">
                <div className="max-w-7xl mx-auto px-4">
                    <ListingResultsView
                        key={`${localities.join(',')}-page-${currentPage}`}
                        localities={localities}
                        listings={searchState.listings}
                        isLoading={isLoading}
                        pageSize={PAGE_SIZE}
                        pagination={searchState.pagination ?? undefined}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>

            <Footer />
        </div>
    );
}